import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import StatCard from '../components/StatCard';
import SeverityBadge from '../components/SeverityBadge';
import DataTable from '../components/DataTable';
import SiteAlertBullet from '../components/SiteAlertBullet';
import useAPI from '../hooks/useAPI';
import {
  getFleetSummary, getProjects, getErrorStats,
} from '../services/api';

const COUNTRY_OPTIONS = [
  { key: 'ALL', label: 'All' },
  { key: 'Israel', label: 'Israel' },
  { key: 'India', label: 'India' },
];

// MongoDB stores country as either 'IL'/'IN' or full names. Normalize.
const matchesCountry = (project, filterKey) => {
  if (filterKey === 'ALL') return true;
  const cc = (project.country || '').trim();
  if (filterKey === 'Israel') return cc === 'Israel' || cc === 'IL';
  if (filterKey === 'India') return cc === 'India' || cc === 'IN';
  return cc === filterKey;
};

export default function FleetOverview() {
  const navigate = useNavigate();
  const { data: summary, loading: loadingSummary } = useAPI(getFleetSummary);
  const { data: projectsData, loading: loadingProjects } = useAPI(getProjects);
  const [countryFilter, setCountryFilter] = useState('ALL');

  // Country-scoped top errors. When 'ALL', use the pre-aggregated summary.
  // When Israel/India, call /api/errors/stats?country=...
  const [countryErrors, setCountryErrors] = useState(null);
  const [loadingCountryErrors, setLoadingCountryErrors] = useState(false);
  useEffect(() => {
    if (countryFilter === 'ALL') {
      setCountryErrors(null);
      return;
    }
    let cancelled = false;
    setLoadingCountryErrors(true);
    getErrorStats(null, 30, countryFilter)
      .then(d => { if (!cancelled) setCountryErrors(d); })
      .catch(e => { if (!cancelled) { console.error(e); setCountryErrors(null); } })
      .finally(() => { if (!cancelled) setLoadingCountryErrors(false); });
    return () => { cancelled = true; };
  }, [countryFilter]);

  const allProjects = projectsData?.projects || [];
  const filteredProjects = useMemo(
    () => allProjects.filter(p => matchesCountry(p, countryFilter)),
    [allProjects, countryFilter]
  );

  if (loadingSummary || loadingProjects) return <PageLoader />;

  // Derive filter-scoped KPI values
  const projectCount = filteredProjects.length;
  const robotTotal = filteredProjects.reduce(
    (sum, p) => sum + ((p.type1_robots || 0) + (p.type2_robots || 0)),
    0
  );

  // Top errors: shape varies by source (fleet summary vs errors/stats)
  const globalTopErrors = summary?.top_error_codes_30d || summary?.top_error_codes_7d || [];
  const countryTopRows = (countryErrors?.error_codes || []).map(e => ({
    error_code: e.code,
    count: e.count,
    robots_count: e.robots_affected,
    severity: e.severity,
  }));
  const topErrorsRows = countryFilter === 'ALL' ? globalTopErrors : countryTopRows;

  const scopeLabel = countryFilter === 'ALL' ? '' : ` — ${countryFilter}`;

  return (
    <div>
      <h1 style={styles.title}>Fleet Overview</h1>
      <p style={styles.subtitle}>
        Real-time status across all AirTouch projects
      </p>

      {/* Data-coverage note */}
      <div style={styles.coverageNote}>
        <span style={styles.coverageIcon}>ⓘ</span>
        <span>
          <strong>Note:</strong> Pilot project, Cramim and Tarabin are not mapped
          in the analytics because they use an older data format that the current
          pipeline does not parse. Numbers and trends on this page exclude them.
        </span>
      </div>

      {/* Country filter bar - affects the whole page */}
      <div style={styles.filterBar}>
        <span style={styles.filterLabel}>Region:</span>
        <div style={styles.countryGroup}>
          {COUNTRY_OPTIONS.map((opt, i, arr) => {
            const active = countryFilter === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => setCountryFilter(opt.key)}
                style={{
                  ...styles.countryBtn,
                  ...(active ? styles.countryBtnActive : {}),
                  borderTopLeftRadius: i === 0 ? 6 : 0,
                  borderBottomLeftRadius: i === 0 ? 6 : 0,
                  borderTopRightRadius: i === arr.length - 1 ? 6 : 0,
                  borderBottomRightRadius: i === arr.length - 1 ? 6 : 0,
                  borderLeft: i === 0 ? '1px solid #dfe6e9' : 'none',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* KPI Cards */}
      <div style={styles.cardGrid}>
        <StatCard label={`Projects${scopeLabel}`} value={projectCount} color="#6c5ce7" large />
        <StatCard
          label={`Total Robots${scopeLabel}`}
          value={robotTotal.toLocaleString()}
          sub={`${projectCount} project${projectCount === 1 ? '' : 's'}`}
          color="#0984e3"
          large
        />
        <StatCard
          label="Errors (30d)"
          value={(
            countryFilter === 'ALL'
              ? (summary?.errors_last_30d || summary?.errors_last_24h || 0)
              : (countryErrors?.total_errors || 0)
          ).toLocaleString()}
          color="#d63031"
        />
        <StatCard
          label={`Top Error${scopeLabel}`}
          value={topErrorsRows[0]?.error_code || '-'}
          sub={topErrorsRows[0] ? `${topErrorsRows[0].count.toLocaleString()} occurrences` : ''}
          color="#e17055"
        />
      </div>

      {/* Two columns */}
      <div style={styles.twoCol}>
        {/* Top errors table */}
        <div>
          <h2 style={styles.sectionTitle}>
            Error Codes (30 days){scopeLabel}
            <span style={styles.sectionCount}>&nbsp;· {topErrorsRows.length}</span>
          </h2>
          {loadingCountryErrors ? (
            <div style={styles.inlineLoader}>Loading country errors...</div>
          ) : (
            <div style={styles.scrollTable}>
              <DataTable
                columns={[
                  { key: 'error_code', label: 'Error Code' },
                  { key: 'count', label: 'Count', align: 'right', render: v => v?.toLocaleString() },
                  { key: 'robots_count', label: 'Robots', align: 'right' },
                  { key: 'severity', label: 'Severity', render: v => <SeverityBadge severity={v} /> },
                ]}
                data={topErrorsRows}
              />
            </div>
          )}
        </div>

        {/* Projects list */}
        <div>
          <h2 style={styles.sectionTitle}>
            Projects ({projectCount}){scopeLabel}
          </h2>
          <div style={styles.scrollTable}>
            <DataTable
              columns={[
                {
                  key: 'name',
                  label: 'Project',
                  render: (v, row) => (
                    <span>
                      <SiteAlertBullet projectId={row.id} />
                      {v}
                    </span>
                  ),
                },
                { key: 'country', label: 'Country' },
                {
                  key: 'type1_robots',
                  label: 'Robots',
                  align: 'right',
                  render: (v, row) => (v || 0) + (row.type2_robots || 0),
                },
              ]}
              data={filteredProjects}
              onRowClick={(row) => navigate(`/projects/${row.id}`)}
            />
          </div>
          {/* Total row */}
          <div style={styles.projectsTotal}>
            <span>
              Total — {projectCount} project{projectCount === 1 ? '' : 's'}
              {countryFilter !== 'ALL' ? ` in ${countryFilter}` : ''}
            </span>
            <span style={{ fontWeight: 700 }}>
              {robotTotal.toLocaleString()} robots
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PageLoader() {
  return (
    <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
      Loading fleet data...
    </div>
  );
}

const styles = {
  title: { fontSize: 24, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 14, color: '#636e72', marginTop: 4, marginBottom: 16 },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
  },
  filterLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#636e72',
  },
  countryGroup: {
    display: 'inline-flex',
  },
  countryBtn: {
    padding: '6px 14px',
    border: '1px solid #dfe6e9',
    borderLeft: 'none',
    background: '#fff',
    color: '#636e72',
    cursor: 'pointer',
    fontSize: 13,
    fontFamily: 'inherit',
    fontWeight: 600,
  },
  countryBtnActive: {
    background: '#6c5ce7',
    color: '#fff',
    borderColor: '#6c5ce7',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 16,
    marginBottom: 32,
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 8,
  },
  sectionCount: {
    fontSize: 13,
    fontWeight: 500,
    color: '#95a5a6',
  },
  coverageNote: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '10px 14px',
    background: '#fff8e1',
    border: '1px solid #ffe082',
    borderRadius: 8,
    color: '#795500',
    fontSize: 12,
    marginBottom: 16,
    lineHeight: 1.5,
  },
  coverageIcon: {
    fontSize: 14,
    fontWeight: 700,
    flexShrink: 0,
  },
  scrollTable: {
    maxHeight: 520,
    overflow: 'auto',
    border: '1px solid #ecf0f1',
    borderRadius: 8,
  },
  inlineLoader: {
    padding: 24,
    color: '#95a5a6',
    fontSize: 13,
    background: '#fafbfc',
    borderRadius: 8,
    textAlign: 'center',
  },
  projectsTotal: {
    marginTop: 8,
    padding: '10px 14px',
    background: '#f5f6fa',
    borderRadius: 8,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 13,
    color: '#2d3436',
    fontWeight: 600,
  },
};
